// Per-child link details (migration 20261022000008).
//
//  - MyLinkDetails: a VIEWER sees their own role on this child's link and sets
//    the short label the family knows them by (set_link_details with
//    p_viewer = themselves and p_role = null, which keeps the role).
//  - ChildAdults: the OWNER sees every adult they share this child with
//    (child_adults is owner-only server-side, so nobody else can ask).
//
// Labels describe the ADULT ("Ms Aisyah, speech"), never the child.

import { useState } from "react";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary } from "../../i18n/dictionary";
import { getPortalData } from "../../data";
import type { LinkRole } from "../../domain/types";
import { Button, Chip, Field, useLoad, type Tone } from "../admin/ui";
import { roleLabel } from "../admin/Kids";

const MAX_LABEL = 60;

const box = {
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-lg)",
  padding: "14px 18px",
  marginBottom: 24,
} as const;

export function MyLinkDetails({
  childId,
  accountId,
  role,
  label,
}: {
  childId: string;
  accountId: string;
  role: LinkRole | null;
  label: string | null;
}) {
  const { locale } = useComfort();
  const t = getDictionary(locale).service.childDetail;
  const [current, setCurrent] = useState(label ?? "");
  const [draft, setDraft] = useState(label ?? "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function save() {
    const next = draft.trim().slice(0, MAX_LABEL);
    setBusy(true);
    setMsg(null);
    try {
      // "" clears the label server-side; null role keeps the role as it is.
      await getPortalData().setLinkDetails(childId, accountId, null, next);
      setCurrent(next);
      setEditing(false);
      setMsg({ ok: true, text: t.labelSaved });
    } catch {
      setMsg({ ok: false, text: t.labelError });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={box}>
      {role && (
        <p style={{ margin: "0 0 8px", fontSize: 14, color: "var(--ink-soft)" }}>
          {fill(t.yourRole, { role: roleLabel(t, role) })}
        </p>
      )}
      {editing ? (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Field
            id={`link-label-${childId}`}
            label={t.yourLabel}
            hint={t.labelHint}
            value={draft}
            maxLength={MAX_LABEL}
            onChange={(e) => setDraft(e.target.value)}
            style={{ flex: "1 1 260px" }}
          />
          <Button onClick={() => void save()} disabled={busy}>
            {t.saveLabel}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setDraft(current);
              setEditing(false);
            }}
            disabled={busy}
          >
            {getDictionary(locale).common.cancel}
          </Button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>{t.yourLabel}:</span>
          <span style={{ fontSize: 15, color: current ? "var(--ink-deep)" : "var(--ink-faint)", overflowWrap: "anywhere" }}>
            {current || t.noLabel}
          </span>
          <Button variant="quiet" onClick={() => setEditing(true)}>
            {t.editLabel}
          </Button>
        </div>
      )}
      <p role="status" aria-live="polite" style={{ margin: msg ? "8px 0 0" : 0, fontSize: 13.5, fontWeight: 600, color: msg?.ok ? "var(--sage-deep)" : "var(--peach-deep)" }}>
        {msg?.text}
      </p>
    </section>
  );
}

export function ChildAdults({ childId }: { childId: string }) {
  const { locale } = useComfort();
  const t = getDictionary(locale).service.childDetail;
  const { data, loading, error } = useLoad(() => getPortalData().childAdults(childId), [childId]);
  if (loading && !data) return null;

  const status = (s: string): { tone: Tone; label: string } =>
    s === "active" ? { tone: "good", label: t.adultActive } : s === "pending" ? { tone: "attention", label: t.adultPending } : { tone: "muted", label: t.adultRevoked };

  return (
    <section aria-labelledby={`adults-${childId}`} style={box}>
      <h2 id={`adults-${childId}`} style={{ fontSize: 15.5, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 10px" }}>
        {t.adultsTitle}
      </h2>
      {error ? (
        <p style={{ margin: 0, fontSize: 14, color: "var(--ink-soft)" }}>{t.adultsError}</p>
      ) : !data || data.length === 0 ? (
        <p style={{ margin: 0, fontSize: 14, color: "var(--ink-soft)" }}>{t.adultsNone}</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {data.map((a) => {
            const st = status(a.status);
            return (
              <li key={a.accountId} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                <span style={{ minWidth: 0 }}>
                  <b style={{ display: "block", fontSize: 14.5, color: "var(--ink-deep)", overflowWrap: "anywhere" }}>{a.label || a.email}</b>
                  <span style={{ fontSize: 12.5, color: "var(--ink-faint)", overflowWrap: "anywhere" }}>
                    {[a.label ? a.email : null, a.orgName].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {a.role && <Chip tone="neutral">{roleLabel(t, a.role)}</Chip>}
                  <Chip tone={st.tone}>{st.label}</Chip>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
